import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { iapService } from '../../services/iap';
import { RootState } from '../../store';
import {
	iapInitRequest,
	iapPurchaseRequest,
	iapReset,
	iapVerifyPurchaseRequest,
} from '../../store/iap/iapActions';
import { Package } from '../../store/types';

export function useIAP() {
	const dispatch = useDispatch();
	const iapState = useSelector((state: RootState) => state.iap);

	/**
	 * Initialize IAP on mount
	 */
	useEffect(() => {
		if (iapService.isIAPSupported() && !iapState.isInitialized) {
			dispatch(iapInitRequest());
		}
	}, [dispatch, iapState.isInitialized]);

	/**
	 * Auto-verify purchases when they come in
	 */
	useEffect(() => {
		if (
			iapState.currentPurchase &&
			iapState.currentPackage &&
			!iapState.verifyLoading &&
			!iapState.purchaseVerified
		) {
			console.log('[useIAP] Dispatching verify request for current purchase:', {
				productId: iapState.currentPurchase.productId,
				transactionId: iapState.currentPurchase.transactionId,
				hasPurchaseToken: Boolean(iapState.currentPurchase.purchaseToken),
				packageId: iapState.currentPackage.id,
				packageType: iapState.currentPackage.type,
				verifyLoading: iapState.verifyLoading,
				purchaseVerified: iapState.purchaseVerified,
			});
			dispatch(
				iapVerifyPurchaseRequest({
					purchase: iapState.currentPurchase,
					pkg: iapState.currentPackage,
					recipientPlayerId: iapState.currentRecipientPlayerId,
				}),
			);
		}
	}, [
		iapState.currentPurchase,
		iapState.currentPackage,
		iapState.currentRecipientPlayerId,
		iapState.verifyLoading,
		iapState.purchaseVerified,
		dispatch,
	]);

	useEffect(() => {
		if (iapState.purchaseVerified) {
			console.log('[useIAP] Purchase verification state changed to success', {
				newBalance: iapState.newBalance,
				pendingPurchases: iapState.pendingPurchases.length,
			});
		}
	}, [iapState.newBalance, iapState.pendingPurchases.length, iapState.purchaseVerified]);

	useEffect(() => {
		if (iapState.purchaseError || iapState.verifyError) {
			console.log('[useIAP] IAP state error updated', {
				purchaseError: iapState.purchaseError,
				verifyError: iapState.verifyError,
				pendingPurchases: iapState.pendingPurchases.map(p => ({
					productId: p.productId,
					transactionId: p.transactionId,
				})),
			});
		}
	}, [iapState.pendingPurchases, iapState.purchaseError, iapState.verifyError]);

	/**
	 * Purchase a package.
	 * Chips use in-app products, while VIP uses subscriptions.
	 * With `recipientPlayerId` it is a chip donation, credited to that player on verify. Check it
	 * with `checkDonation` first: the store charges before the backend sees anything.
	 */
	const purchasePackage = useCallback(
		(pkg: Package, recipientPlayerId?: string) => {
			const productId = iapService.mapPackageToProductId(pkg);

			if (!productId) {
				console.error('[useIAP] Could not get product ID from package metadata:', pkg);
				console.error(
					'[useIAP] Package must have metadata.appleStore (iOS) or metadata.googlePay (Android)',
				);
				return;
			}

			const isSubscription = pkg.type === 'VIP_MEMBERSHIP';

			console.log('[useIAP] Purchasing package:', {
				packageId: pkg.id,
				packageName: pkg.name,
				productId,
				isSubscription,
			});

			dispatch(
				iapPurchaseRequest({
					productId,
					pkg,
					isSubscription,
					recipientPlayerId,
				}),
			);
		},
		[dispatch],
	);

	/**
	 * Reset IAP state
	 */
	const resetIAP = useCallback(() => {
		dispatch(iapReset());
	}, [dispatch]);

	/**
	 * Check if IAP is supported on this platform
	 */
	const isSupported = iapService.isIAPSupported();

	return {
		// State
		isSupported,
		isInitialized: iapState.isInitialized,
		initLoading: iapState.initLoading,
		products: iapState.products,
		subscriptions: iapState.subscriptions,
		productsLoading: iapState.productsLoading,
		subscriptionsLoading: iapState.subscriptionsLoading,
		purchaseLoading: iapState.purchaseLoading,
		purchaseError: iapState.purchaseError,
		verifyLoading: iapState.verifyLoading,
		verifyError: iapState.verifyError,
		purchaseVerified: iapState.purchaseVerified,
		newBalance: iapState.newBalance,
		currentPurchase: iapState.currentPurchase,
		currentPackage: iapState.currentPackage,

		// Actions
		purchasePackage,
		resetIAP,
	};
}
